import React, { useEffect, useState } from "react"
import { Container, Card, CardBody, Modal, Form, Label, Input } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import Select from "react-select"
import Swal from "sweetalert2"
import axios from "axios"
import configure from "configure"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import { fetchNodeData, FetchNodeDeleteData } from "utils/common-fetchData"
import { formatDate } from "utils/formatDate"

const EMPTY_FORM = {
  title: "",
  roles: [],
  departments: [],
  userIds: [],
  file: null,
}

const ZithasSOP = () => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState("sop")

  // SOP Documents state
  const [sopList, setSopList] = useState([])
  const [modal, setModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)

  // Shared lookup state
  const [Roles, setRoles] = useState([])
  const [Departments, setDepartments] = useState([])
  const [Staff, setStaff] = useState([])

  // Training Docs Access state
  const [folderAccess, setFolderAccess] = useState([])
  const [folderRoleInputs, setFolderRoleInputs] = useState({})
  const [savingFolder, setSavingFolder] = useState(null)
  const [savingAll, setSavingAll] = useState(false)

  useEffect(() => {
    document.title = "Zithas SOP | Zithas Technologies"
    fetchAll()
  }, [])

  // Initialise per-folder role selects once both Roles + folderAccess are loaded
  useEffect(() => {
    if (!Roles.length || !folderAccess.length) return
    const inputs = {}
    for (const row of folderAccess) {
      const ids = row.roles
        ? String(row.roles)
            .split(",")
            .map(s => s.trim())
        : []
      inputs[row.folder_key] = Roles.filter(r => ids.includes(String(r.value)))
    }
    setFolderRoleInputs(inputs)
  }, [Roles, folderAccess])

  const fetchAll = async () => {
    await Promise.all([
      fetchSOPs(),
      fetchRoles(),
      fetchDepartments(),
      fetchStaff(),
      fetchFolderAccess(),
    ])
  }

  const fetchSOPs = async () => {
    const token = secureLocalStorage.getItem("auth_token")
    const res = await fetchNodeData(setLoading, "sop/getAll", token)
    if (res?.success) setSopList(res.data || [])
  }

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${configure.nodeApi}/staffData`, {
        headers: { Accept: "application/json" },
      })
      setRoles(
        (res.data?.data?.roles || []).map(r => ({
          value: r.id,
          label: r.role_name,
        }))
      )
    } catch (e) {
      console.log("Roles fetch error", e)
    }
  }

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${configure.nodeApi}/departments`, {
        headers: { Accept: "application/json" },
      })
      setDepartments(
        (res.data?.data?.department || []).map(d => ({
          value: d.id,
          label: d.department_name,
        }))
      )
    } catch (e) {
      console.log("Departments fetch error", e)
    }
  }

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${configure.nodeApi}/staff`, {
        headers: { Accept: "application/json" },
      })
      const list = res.data?.data ?? res.data ?? []
      setStaff(
        Array.isArray(list)
          ? list.map(s => ({
              value: s.id,
              label: s.name || s.log_name || `Staff #${s.id}`,
            }))
          : []
      )
    } catch (e) {
      console.log("Staff fetch error", e)
    }
  }

  const fetchFolderAccess = async () => {
    try {
      const token = secureLocalStorage.getItem("auth_token")
      const res = await axios.get(`${configure.nodeApi}/sop/folderAccess`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
      setFolderAccess(res.data?.data || [])
    } catch (e) {
      console.log("FolderAccess fetch error", e)
    }
  }

  // ── SOP Document handlers ──

  const openAddModal = () => {
    setEditId(null)
    setFormData(EMPTY_FORM)
    setModal(true)
  }

  const openEditModal = sop => {
    setEditId(sop.id)
    const roleIds = sop.roles
      ? String(sop.roles)
          .split(",")
          .map(s => s.trim())
      : []
    const deptIds = sop.departments
      ? String(sop.departments)
          .split(",")
          .map(s => s.trim())
      : []
    const userIdArr = sop.user_ids
      ? String(sop.user_ids)
          .split(",")
          .map(s => s.trim())
      : []
    setFormData({
      title: sop.title || "",
      roles: Roles.filter(r => roleIds.includes(String(r.value))),
      departments: Departments.filter(d => deptIds.includes(String(d.value))),
      userIds: Staff.filter(s => userIdArr.includes(String(s.value))),
      file: null,
    })
    setModal(true)
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!editId && !formData.file) {
      Swal.fire({
        icon: "warning",
        title: "Please select an HTML file to upload.",
        confirmButtonColor: "#752a97",
      })
      return
    }

    const fd = new FormData()
    fd.append("title", formData.title)
    fd.append("roles", formData.roles.map(r => r.value).join(",") || "")
    fd.append(
      "departments",
      formData.departments.map(d => d.value).join(",") || ""
    )
    fd.append("user_ids", formData.userIds.map(s => s.value).join(",") || "")
    if (formData.file) fd.append("file", formData.file)
    if (!editId) fd.append("user_id", localStorage.getItem("auth_id"))

    setLoading(true)
    try {
      const token = secureLocalStorage.getItem("auth_token")
      const res = editId
        ? await axios.put(`${configure.nodeApi}/sop/edit/${editId}`, fd, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          })
        : await axios.post(`${configure.nodeApi}/sop/add`, fd, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          })

      if (res.data?.success) {
        Swal.fire({
          icon: "success",
          title: editId
            ? "SOP updated successfully"
            : "SOP uploaded successfully",
          timer: 1500,
          showConfirmButton: false,
        })
        setModal(false)
        fetchSOPs()
      } else {
        Swal.fire({
          icon: "error",
          title: editId ? "Update failed" : "Upload failed",
          text: res.data?.message,
          confirmButtonColor: "#752a97",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: editId ? "Update failed" : "Upload failed",
        text: err.message,
        confirmButtonColor: "#752a97",
      })
    }
    setLoading(false)
  }

  const handleDelete = async id => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This SOP will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    })
    if (result.isConfirmed) {
      const res = await FetchNodeDeleteData(setLoading, "sop/delete", id)
      if (res?.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          timer: 1200,
          showConfirmButton: false,
        })
        fetchSOPs()
      } else {
        Swal.fire({ icon: "error", title: "Something went wrong." })
      }
    }
  }

  // ── Training Docs Access handlers ──

  const handleSaveFolderAccess = async folder_key => {
    setSavingFolder(folder_key)
    try {
      const token = secureLocalStorage.getItem("auth_token")
      const roles =
        (folderRoleInputs[folder_key] || []).map(r => r.value).join(",") || ""
      await axios.put(
        `${configure.nodeApi}/sop/folderAccess/${encodeURIComponent(
          folder_key
        )}`,
        { roles },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      Swal.fire({
        icon: "success",
        title: "Access updated",
        timer: 1200,
        showConfirmButton: false,
      })
      fetchFolderAccess()
    } catch (err) {
      Swal.fire({ icon: "error", title: "Save failed", text: err.message })
    }
    setSavingFolder(null)
  }

  const handleSaveAllFolderAccess = async () => {
    setSavingAll(true)
    try {
      const token = secureLocalStorage.getItem("auth_token")
      const folders = folderAccess.map(row => ({
        folder_key: row.folder_key,
        folder_label: row.folder_label,
        roles:
          (folderRoleInputs[row.folder_key] || [])
            .map(r => r.value)
            .join(",") || "",
      }))
      await axios.post(
        `${configure.nodeApi}/sop/folderAccess/bulk`,
        { folders },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      Swal.fire({
        icon: "success",
        title: "All folder access saved",
        timer: 1500,
        showConfirmButton: false,
      })
      fetchFolderAccess()
    } catch (err) {
      Swal.fire({ icon: "error", title: "Save failed", text: err.message })
    }
    setSavingAll(false)
  }

  // ── Label helpers ──

  const getRoleLabels = roles => {
    if (!roles)
      return (
        <span className="text-muted" style={{ fontSize: 12 }}>
          All Roles
        </span>
      )
    const ids = String(roles)
      .split(",")
      .map(s => s.trim())
    const found = Roles.filter(r => ids.includes(String(r.value)))
    if (!found.length)
      return (
        <span className="text-muted" style={{ fontSize: 12 }}>
          All Roles
        </span>
      )
    return (
      <div className="d-flex flex-wrap gap-1">
        {found.map(r => (
          <span
            key={r.value}
            className="badge bg-white text-dark border"
            style={{ fontSize: 11 }}
          >
            {r.label}
          </span>
        ))}
      </div>
    )
  }

  const getDeptLabels = depts => {
    if (!depts)
      return (
        <span className="text-muted" style={{ fontSize: 12 }}>
          All Departments
        </span>
      )
    const ids = String(depts)
      .split(",")
      .map(s => s.trim())
    const found = Departments.filter(d => ids.includes(String(d.value)))
    if (!found.length)
      return (
        <span className="text-muted" style={{ fontSize: 12 }}>
          All Departments
        </span>
      )
    return (
      <div className="d-flex flex-wrap gap-1">
        {found.map(d => (
          <span
            key={d.value}
            className="badge bg-white text-dark border"
            style={{ fontSize: 11 }}
          >
            {d.label}
          </span>
        ))}
      </div>
    )
  }

  const getUserLabels = user_ids => {
    if (!user_ids)
      return (
        <span className="text-muted" style={{ fontSize: 12 }}>
          —
        </span>
      )
    const ids = String(user_ids)
      .split(",")
      .map(s => s.trim())
    const found = Staff.filter(s => ids.includes(String(s.value)))
    if (!found.length)
      return (
        <span className="text-muted" style={{ fontSize: 12 }}>
          —
        </span>
      )
    return (
      <div className="d-flex flex-wrap gap-1">
        {found.map(s => (
          <span
            key={s.value}
            className="badge bg-white text-dark border"
            style={{ fontSize: 11 }}
          >
            {s.label}
          </span>
        ))}
      </div>
    )
  }

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content mb-4">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Zithas SOP" />
          <Card>
            <CardBody>
              {/* ── Header ── */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: "linear-gradient(135deg, #752a97, #a855c9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className="fas fa-file-alt text-white"
                      style={{ fontSize: 15 }}
                    ></i>
                  </div>
                  <span
                    className="fw-bold text-purple"
                    style={{ fontSize: 15 }}
                  >
                    Zithas SOP
                  </span>
                </div>

                {activeTab === "sop" && (
                  <button
                    className="border-radius btn btn-purple btn-sm"
                    onClick={openAddModal}
                  >
                    <i className="fas fa-plus me-2"></i>Add SOP
                  </button>
                )}
                {activeTab === "training" && (
                  <button
                    className="border-radius btn btn-purple btn-sm"
                    onClick={handleSaveAllFolderAccess}
                    disabled={savingAll}
                  >
                    <i className="fas fa-save me-2"></i>
                    {savingAll ? "Saving…" : "Save All"}
                  </button>
                )}
              </div>

              {/* ── Tab Toggle ── */}
              <ul className="nav nav-tabs mb-4" style={{ borderBottom: "2px solid #dee2e6" }}>
                <li className="nav-item">
                  <span
                    onClick={() => setActiveTab("sop")}
                    className="nav-link"
                    style={{
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      color: activeTab === "sop" ? "#752a97" : "#6c757d",
                      borderColor: activeTab === "sop" ? "#dee2e6 #dee2e6 #fff" : "transparent",
                      borderBottom: activeTab === "sop" ? "2px solid #752a97" : "none",
                      background: "transparent",
                    }}
                  >
                    <i className="fas fa-file-alt me-2"></i>SOP Documents
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    onClick={() => setActiveTab("training")}
                    className="nav-link"
                    style={{
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      color: activeTab === "training" ? "#752a97" : "#6c757d",
                      borderColor: activeTab === "training" ? "#dee2e6 #dee2e6 #fff" : "transparent",
                      borderBottom: activeTab === "training" ? "2px solid #752a97" : "none",
                      background: "transparent",
                    }}
                  >
                    <i className="fas fa-folder-open me-2"></i>Training Docs Access
                  </span>
                </li>
              </ul>

              {/* ── Tab: SOP Documents ── */}
              {activeTab === "sop" && (
                <>
                  {sopList.length === 0 && !loading ? (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-folder-open fa-3x mb-3 d-block"></i>
                      <p>
                        No SOPs uploaded yet. Click <strong>Add SOP</strong> to
                        get started.
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table
                        className="table table-bordered mb-0"
                        style={{ fontSize: 13 }}
                      >
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 40 }}>#</th>
                            <th>Title</th>
                            <th>Roles</th>
                            <th>Departments</th>
                            <th>Specific Users</th>
                            <th style={{ width: 120 }}>Uploaded On</th>
                            <th className="text-center" style={{ width: 120 }}>
                              Document
                            </th>
                            <th className="text-center" style={{ width: 100 }}>
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sopList.map((sop, i) => (
                            <tr key={sop.id} className="row-hover">
                              <td>{i + 1}</td>
                              <td className="fw-semibold">{sop.title}</td>
                              <td>{getRoleLabels(sop.roles)}</td>
                              <td>{getDeptLabels(sop.departments)}</td>
                              <td>{getUserLabels(sop.user_ids)}</td>
                              <td>{formatDate(sop.created_at)}</td>
                              <td className="text-center">
                                <a
                                  href={sop.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                  title="View / Download"
                                >
                                  <i className="fas fa-file-download me-1"></i>
                                  View
                                </a>
                              </td>
                              <td className="text-center">
                                <div className="d-flex gap-1 justify-content-center">
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => openEditModal(sop)}
                                    title="Edit"
                                  >
                                    <i className="fas fa-pencil-alt"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(sop.id)}
                                    title="Delete"
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* ── Tab: Training Docs Access ── */}
              {activeTab === "training" && (
                <>
                  <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                    Assign which roles can access each training document folder.
                    Leave empty to allow all roles.
                  </p>
                  {folderAccess.length === 0 && !loading ? (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-folder fa-3x mb-3 d-block"></i>
                      <p>No training document folders found.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table
                        className="table table-bordered mb-0"
                        style={{ fontSize: 13 }}
                      >
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th style={{ width: 250 }}>Folder</th>
                            <th>Roles with Access</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {folderAccess.map((row, i) => (
                            <tr key={row.folder_key}>
                              <td>{i + 1}</td>
                              <td>
                                <span className="fw-semibold">
                                  {row.folder_label}
                                </span>
                                <div style={{ fontSize: 11, color: "#aaa" }}>
                                  {row.folder_key}
                                </div>
                              </td>
                              <td>
                                <Select
                                  placeholder="All roles (leave empty for unrestricted)…"
                                  className="basic-multi-select"
                                  classNamePrefix="select"
                                  options={Roles}
                                  value={folderRoleInputs[row.folder_key] || []}
                                  onChange={val =>
                                    setFolderRoleInputs(prev => ({
                                      ...prev,
                                      [row.folder_key]: val || [],
                                    }))
                                  }
                                  isMulti
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: base => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              </td>
                              <td className="text-center">
                                <button
                                  className="border-radius btn btn-purple btn-sm px-3"
                                  onClick={() =>
                                    handleSaveFolderAccess(row.folder_key)
                                  }
                                  disabled={savingFolder === row.folder_key}
                                >
                                  {savingFolder === row.folder_key ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    <>
                                      <i className="fas fa-save me-1"></i>Save
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* ── Add / Edit SOP Modal ── */}
      <Modal isOpen={modal} size="lg" centered toggle={() => setModal(false)}>
        <div className="modal-header px-4 py-3">
          <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
            <i
              className={`fas ${
                editId ? "fa-edit" : "fa-file-upload"
              } me-2 text-purple`}
            ></i>
            {editId ? "Edit SOP" : "Upload SOP"}
          </h5>
          <button
            type="button"
            onClick={() => setModal(false)}
            className="btn-modal-close"
          >
            <span className="btn-modal-span">&times;</span>
          </button>
        </div>

        <div className="modal-body px-4 py-3">
          <Form onSubmit={handleSubmit}>
            {/* Title */}
            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>
                SOP Title <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter SOP title"
                value={formData.title}
                onChange={e =>
                  setFormData(prev => ({ ...prev, title: e.target.value }))
                }
                required
              />
            </div>

            {/* Document upload */}
            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>
                HTML Document{" "}
                {!editId && <span className="text-danger">*</span>}
                <span
                  className="text-muted ms-1"
                  style={{ fontWeight: 400, fontSize: 11 }}
                >
                  {editId
                    ? "(.html — leave blank to keep existing)"
                    : "(.html files only)"}
                </span>
              </Label>
              <Input
                type="file"
                accept=".html,.htm"
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    file: e.target.files[0] || null,
                  }))
                }
              />
            </div>

            <div className="row g-3 mb-3">
              {/* Roles */}
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Roles{" "}
                  <span
                    className="text-muted"
                    style={{ fontWeight: 400, fontSize: 11 }}
                  >
                    (leave empty for all)
                  </span>
                </Label>
                <Select
                  placeholder="Select roles…"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  options={Roles}
                  value={formData.roles}
                  onChange={val =>
                    setFormData(prev => ({ ...prev, roles: val || [] }))
                  }
                  isMulti
                />
              </div>

              {/* Departments */}
              <div className="col-md-6">
                <Label className="mb-1" style={{ fontSize: 12 }}>
                  Departments{" "}
                  <span
                    className="text-muted"
                    style={{ fontWeight: 400, fontSize: 11 }}
                  >
                    (leave empty for all)
                  </span>
                </Label>
                <Select
                  placeholder="Select departments…"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  options={Departments}
                  value={formData.departments}
                  onChange={val =>
                    setFormData(prev => ({ ...prev, departments: val || [] }))
                  }
                  isMulti
                />
              </div>
            </div>

            {/* Specific Staff */}
            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>
                Specific Staff{" "}
                <span
                  className="text-muted"
                  style={{ fontWeight: 400, fontSize: 11 }}
                >
                  (optional — grant access to individual users regardless of
                  role/dept)
                </span>
              </Label>
              <Select
                placeholder="Select staff members…"
                className="basic-multi-select"
                classNamePrefix="select"
                options={Staff}
                value={formData.userIds}
                onChange={val =>
                  setFormData(prev => ({ ...prev, userIds: val || [] }))
                }
                isMulti
              />
            </div>

            <div
              className="mt-4 pt-3"
              style={{ borderTop: "1px solid #e9ecef" }}
            >
              <button
                type="submit"
                className="border-radius btn btn-purple btn-sm px-4"
              >
                <i
                  className={`fas ${editId ? "fa-save" : "fa-upload"} me-2`}
                ></i>
                {editId ? "Update SOP" : "Upload SOP"}
              </button>
              <button
                type="button"
                className="border-radius btn btn-light btn-sm px-4 ms-2"
                onClick={() => setModal(false)}
              >
                Cancel
              </button>
            </div>
          </Form>
        </div>
      </Modal>
    </React.Fragment>
  )
}

export default ZithasSOP
